import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import WorkflowDiagram from './WorkflowDiagram';

const API_BASE = process.env.REACT_APP_API_URL || '';

const STEP_TYPES = [
  { value: 'ai_prompt',         label: 'KI-Prompt' },
  { value: 'http_request',      label: 'HTTP-Request' },
  { value: 'data_source_query', label: 'Datenquelle abfragen' },
  { value: 'transform',         label: 'Daten transformieren' },
  { value: 'condition',         label: 'Bedingung' },
  { value: 'create_task',       label: 'Aufgabe anlegen' },
  { value: 'email_send',        label: 'E-Mail senden' }
];

const DEFAULT_CONFIG = {
  ai_prompt:         { prompt: '', model: '' },
  http_request:      { method: 'GET', url: '', headers: {}, body: '' },
  data_source_query: { dataSourceId: '', method: 'GET', path: '', body: '', query: '' },
  transform:         { template: {} },
  condition:         { expression: '', then: null, else: null },
  create_task:       { title: '', assigneeUserId: '', assigneeDepartmentId: '', due: '', payload: {} },
  email_send:        { to: '', cc: '', bcc: '', subject: '', body: '', replyTo: '' }
};

function uniqueId(prefix = 'step') {
  return `${prefix}_${Math.random().toString(36).slice(2, 8)}`;
}

// Anzeige-Label für Modell-Dropdowns: bevorzugt den vom Admin gesetzten
// Anzeigenamen, fällt auf den echten Modellnamen zurück. Der `value` der
// Option bleibt immer m.name — Ollama kennt nur diesen.
function formatModelLabel(m) {
  const size = m.size ? ` (${(m.size / 1e9).toFixed(1)} GB)` : '';
  return m.displayName ? `${m.displayName}${size}` : `${m.name}${size}`;
}

export default function WorkflowEditor({ initial, onClose, onSaved, defaultModel, readOnly }) {
  const { token } = useAuth();
  const { tenantFetch, activeTenant } = useTenant();
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [requirement, setRequirement] = useState(initial?.requirement || '');
  const [visibility, setVisibility] = useState(initial?.visibility || 'private');
  const [steps, setSteps] = useState(initial?.steps || []);
  const [schedule, setSchedule] = useState(() => {
    const s = initial?.schedule || {};
    return {
      enabled: !!s.enabled,
      frequency: s.frequency || 'daily',
      // Backwards-compat: alte Workflows haben evtl. nur timeOfDay
      startTime: s.startTime || s.timeOfDay || '09:00',
      endTime: s.endTime || '',
      dayOfWeek: Number.isInteger(s.dayOfWeek) ? s.dayOfWeek : 1,
      dailyLimit: s.dailyLimit || 0,
      intervalMinutes: s.intervalMinutes || 0,
      startDate: s.startDate ? String(s.startDate).slice(0, 10) : ''
    };
  });
  const [activeTab, setActiveTab] = useState('general'); // 'general' | 'steps' | 'diagram' | 'schedule'
  const [dataSources, setDataSources] = useState([]);
  const [models, setModels] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [tenantMembers, setTenantMembers] = useState([]);
  const [generationModel, setGenerationModel] = useState(defaultModel || '');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showRequirement, setShowRequirement] = useState(!initial);
  // KI-Generator hat zwei Modi:
  //   'create' — Workflow von Grund auf aus `requirement` erzeugen (überschreibt steps)
  //   'modify' — bestehenden Workflow anhand von `editRequest` anpassen (KI bekommt aktuellen Stand mit)
  // Default: wenn Schritte da sind → modify, sonst create.
  const [aiMode, setAiMode] = useState(() => (initial?.steps?.length ? 'modify' : 'create'));
  const [editRequest, setEditRequest] = useState('');

  useEffect(() => {
    tenantFetch('/api/data-sources').then(r => r.json()).then(d => setDataSources(d.dataSources || [])).catch(() => {});
    tenantFetch('/api/departments').then(r => r.json()).then(d => setDepartments(d.departments || [])).catch(() => {});
    if (activeTenant?._id) {
      tenantFetch(`/api/tenants/${activeTenant._id}/members`)
        .then(r => r.json())
        .then(d => setTenantMembers(d.members || []))
        .catch(() => {});
    }
  }, [tenantFetch, activeTenant]);

  useEffect(() => {
    fetch(`${API_BASE}/api/models`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const list = d.models || [];
        setModels(list);
        if (!generationModel && list.length) setGenerationModel(list[0].name);
      })
      .catch(() => {});
  }, [token]);

  const handleGenerate = async () => {
    const isModify = aiMode === 'modify' && steps.length > 0;
    const userText = isModify ? editRequest : requirement;
    if (!userText.trim()) {
      setError(isModify ? 'Bitte beschreibe deinen Bearbeitungswunsch.' : 'Bitte beschreibe die Anforderung.');
      return;
    }
    setError('');
    setGenerating(true);
    try {
      const body = {
        requirement: userText,
        dataSourceIds: dataSources.map(d => d._id),
        model: generationModel || defaultModel,
        mode: isModify ? 'modify' : 'create'
      };
      if (isModify) {
        body.currentWorkflow = { name, description, steps };
      }
      const res = await tenantFetch('/api/workflows/generate', {
        method: 'POST',
        body: JSON.stringify(body)
      });
      // Der Generator antwortet als SSE (mit Heartbeats), damit lange Läufe
      // nicht an Reverse-Proxy-Timeouts scheitern. Frühe Validierungsfehler
      // (leere Anforderung, kein Modell) kommen weiterhin als normales JSON.
      const ctype = res.headers.get('content-type') || '';
      let draft;
      if (ctype.includes('application/json')) {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Fehler beim Generieren');
        draft = data.draft || {};
      } else {
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        let errMsg = null;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const parts = buf.split('\n\n');
          buf = parts.pop();
          for (const part of parts) {
            const dataLine = part.split('\n').find(l => l.startsWith('data:'));
            if (!dataLine) continue; // Heartbeat-Kommentar (": keepalive")
            let j;
            try { j = JSON.parse(dataLine.slice(5).trim()); } catch { continue; }
            if (j.error) errMsg = j.error;
            if (j.draft) draft = j.draft;
          }
        }
        if (errMsg) throw new Error(errMsg);
        if (!draft) throw new Error('Keine Antwort vom Generator erhalten.');
      }
      if (isModify) {
        // Anpassung: Name und Beschreibung nur übernehmen, wenn die KI sie wirklich
        // sinnvoll geändert hat — und IMMER die neuen Schritte.
        if (draft.name) setName(draft.name);
        if (typeof draft.description === 'string') setDescription(draft.description);
        if (Array.isArray(draft.steps)) setSteps(draft.steps);
        // Bearbeitungswunsch leeren — er war ein einmaliger Auftrag, nicht persistent
        setEditRequest('');
      } else {
        // Neu erzeugen: nur dann übernehmen, wenn Felder noch leer sind
        if (draft.name && !name) setName(draft.name);
        if (draft.description && !description) setDescription(draft.description);
        if (Array.isArray(draft.steps)) setSteps(draft.steps);
        setShowRequirement(false);
        // Nach dem Erstellen sofort in Modify-Modus wechseln, damit der Nutzer
        // direkt weiter anpassen kann.
        setAiMode('modify');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('Name ist erforderlich.'); return; }
    setSaving(true);
    setError('');
    try {
      const body = { name, description, requirement, steps, visibility, schedule };
      let res;
      if (initial?._id) {
        res = await tenantFetch(`/api/workflows/${initial._id}`, { method: 'PATCH', body: JSON.stringify(body) });
      } else {
        res = await tenantFetch('/api/workflows', { method: 'POST', body: JSON.stringify(body) });
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler beim Speichern');
      onSaved && onSaved(data.workflow);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const addStep = (type) => {
    setSteps(prev => [
      ...prev,
      { id: uniqueId(), type, name: STEP_TYPES.find(t => t.value === type)?.label || type, config: { ...DEFAULT_CONFIG[type] } }
    ]);
  };

  const moveStep = (idx, dir) => {
    setSteps(prev => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const removeStep = (idx) => {
    setSteps(prev => prev.filter((_, i) => i !== idx));
  };

  const updateStep = (idx, patch) => {
    setSteps(prev => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
  };

  const updateConfig = (idx, key, value) => {
    setSteps(prev => prev.map((s, i) => i === idx ? { ...s, config: { ...s.config, [key]: value } } : s));
  };

  return (
    <div className="workflow-editor-overlay" onClick={onClose}>
      <div className="workflow-editor" onClick={e => e.stopPropagation()}>
        <div className="workflow-editor-header">
          <h2>{initial?._id ? 'Workflow bearbeiten' : 'Neuer Workflow'}</h2>
          <button className="icon-btn" onClick={onClose}>✕</button>
        </div>

        <div className="workflow-editor-tabs">
          <button
            className={`workflow-editor-tab ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >📋 Allgemein</button>
          <button
            className={`workflow-editor-tab ${activeTab === 'steps' ? 'active' : ''}`}
            onClick={() => setActiveTab('steps')}
          >🧩 Schritte ({steps.length})</button>
          <button
            className={`workflow-editor-tab ${activeTab === 'diagram' ? 'active' : ''}`}
            onClick={() => setActiveTab('diagram')}
          >🗺 Diagramm</button>
          <button
            className={`workflow-editor-tab ${activeTab === 'schedule' ? 'active' : ''}`}
            onClick={() => setActiveTab('schedule')}
          >⏱ Zeitplan{schedule.enabled ? ' •' : ''}</button>
        </div>

        <div className="workflow-editor-body">
          {activeTab === 'general' && (
            <div className="workflow-tab-panel">
              <div className="workflow-meta">
                <label className="data-source-field">
                  <span>Name *</span>
                  <input className="text-input" value={name} onChange={e => setName(e.target.value)} disabled={readOnly} />
                </label>
                <label className="data-source-field">
                  <span>Beschreibung</span>
                  <input className="text-input" value={description} onChange={e => setDescription(e.target.value)} disabled={readOnly} />
                </label>
                <label className="data-source-field">
                  <span>Sichtbarkeit</span>
                  <select value={visibility} onChange={e => setVisibility(e.target.value)} disabled={readOnly}>
                    <option value="private">Privat (nur ich)</option>
                    <option value="tenant">Mandant (alle Mitglieder)</option>
                  </select>
                </label>
              </div>
              <p className="section-hint">
                Diese Angaben sind allgemein. Schritte (Logik) und Zeitplan findest du in den anderen Tabs.
              </p>
            </div>
          )}

          {activeTab === 'steps' && (
            <div className="workflow-tab-panel">
              {!readOnly && (
                <div className="workflow-ai-block">
                  <button
                    className="link-btn"
                    onClick={() => setShowRequirement(v => !v)}
                    style={{ marginBottom: 6 }}
                  >
                    {showRequirement ? '▾ KI-Generator ausblenden' : '▸ KI-Generator anzeigen'}
                  </button>
                  {showRequirement && (
                    <>
                      {/* Modus-Umschalter — nur sinnvoll wenn schon Schritte existieren */}
                      {steps.length > 0 && (
                        <div className="workflow-ai-mode-tabs">
                          <button
                            type="button"
                            className={`workflow-ai-mode-tab ${aiMode === 'modify' ? 'active' : ''}`}
                            onClick={() => setAiMode('modify')}
                          >
                            ✏️ Anpassen
                          </button>
                          <button
                            type="button"
                            className={`workflow-ai-mode-tab ${aiMode === 'create' ? 'active' : ''}`}
                            onClick={() => setAiMode('create')}
                          >
                            🔄 Neu erzeugen
                          </button>
                        </div>
                      )}

                      {aiMode === 'modify' && steps.length > 0 ? (
                        <>
                          {requirement && (
                            <label className="data-source-field">
                              <span>Original-Anforderung (Referenz, nicht editierbar)</span>
                              <textarea
                                className="text-input"
                                rows={2}
                                value={requirement}
                                readOnly
                                style={{ opacity: 0.7 }}
                              />
                            </label>
                          )}
                          <label className="data-source-field">
                            <span>Bearbeitungswunsch</span>
                            <textarea
                              className="text-input"
                              rows={4}
                              value={editRequest}
                              onChange={e => setEditRequest(e.target.value)}
                              placeholder="z.B. Füge am Ende einen Schritt zum E-Mail-Versand hinzu. Oder: Ändere Schritt 2 zu einem POST-Request."
                            />
                          </label>
                          <p className="workflow-ai-hint" style={{ display: 'block' }}>
                            Die KI bekommt den aktuellen Workflow ({steps.length} Schritt(e)) als Kontext und passt nur das an, was du beschreibst. Nicht erwähnte Schritte bleiben erhalten.
                          </p>
                        </>
                      ) : (
                        <label className="data-source-field">
                          <span>Anforderung in natürlicher Sprache</span>
                          <textarea
                            className="text-input"
                            rows={4}
                            value={requirement}
                            onChange={e => setRequirement(e.target.value)}
                            placeholder="z.B. Wenn eine neue Kundenbestellung eingeht, prüfe die Lieferadresse, lege eine Aufgabe für die Kommissionierung an und sende eine Bestätigungs-E-Mail."
                          />
                          {steps.length > 0 && aiMode === 'create' && (
                            <p className="workflow-ai-hint" style={{ display: 'block', color: '#e0a000' }}>
                              ⚠ Achtung: Im „Neu erzeugen"-Modus werden die aktuellen {steps.length} Schritt(e) komplett überschrieben.
                            </p>
                          )}
                        </label>
                      )}

                      <div className="workflow-ai-row">
                        <label className="data-source-field">
                          <span>KI-Modell für Generierung</span>
                          <select
                            value={generationModel}
                            onChange={e => setGenerationModel(e.target.value)}
                          >
                            {models.length === 0 && <option value="">— kein Modell verfügbar —</option>}
                            {models.map(m => (
                              <option key={m.name} value={m.name}>
                                {formatModelLabel(m)}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <div className="workflow-ai-actions">
                        <span className="workflow-ai-hint">
                          Verfügbare Datenquellen: {dataSources.length === 0 ? 'keine' : dataSources.map(d => d.name).join(', ')}
                        </span>
                        <button
                          className="primary-btn"
                          onClick={handleGenerate}
                          disabled={
                            generating ||
                            !generationModel ||
                            (aiMode === 'modify' && steps.length > 0
                              ? !editRequest.trim()
                              : !requirement.trim())
                          }
                        >
                          {generating
                            ? (aiMode === 'modify' && steps.length > 0 ? 'Passe an…' : 'Generiere…')
                            : (aiMode === 'modify' && steps.length > 0 ? '✨ Workflow anpassen' : '✨ Workflow generieren')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="workflow-steps">
                <div className="workflow-steps-header">
                  <h3>Schritte ({steps.length})</h3>
                  {!readOnly && (
                    <select
                      className="workflow-add-step"
                      onChange={e => { if (e.target.value) { addStep(e.target.value); e.target.value = ''; } }}
                      value=""
                    >
                      <option value="">+ Schritt hinzufügen…</option>
                      {STEP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  )}
                </div>

                {steps.length === 0 ? (
                  <p className="workflow-empty">Noch keine Schritte. Generiere einen Workflow oder füge manuell Schritte hinzu.</p>
                ) : (
                  <ol className="workflow-step-list">
                    {steps.map((step, idx) => (
                      <li key={step.id} className="workflow-step">
                        <div className="workflow-step-head">
                          <span className="workflow-step-index">{idx + 1}</span>
                          <input
                            className="text-input workflow-step-name"
                            value={step.name || ''}
                            onChange={e => updateStep(idx, { name: e.target.value })}
                            disabled={readOnly}
                          />
                          <span className="workflow-step-type-tag">{STEP_TYPES.find(t => t.value === step.type)?.label || step.type}</span>
                          {!readOnly && (
                            <div className="workflow-step-controls">
                              <button className="icon-btn" onClick={() => moveStep(idx, -1)} title="Hoch">▲</button>
                              <button className="icon-btn" onClick={() => moveStep(idx, 1)} title="Runter">▼</button>
                              <button className="icon-btn danger" onClick={() => removeStep(idx)} title="Entfernen">✕</button>
                            </div>
                          )}
                        </div>
                        <StepConfig step={step} idx={idx} dataSources={dataSources} models={models} departments={departments} tenantMembers={tenantMembers} updateConfig={updateConfig} readOnly={readOnly} />
                        <div className="workflow-step-runif">
                          <label className="workflow-step-runif-label">
                            Nur ausführen wenn (optional):
                            <input
                              className="text-input"
                              value={step.runIf || ''}
                              onChange={e => updateStep(idx, { runIf: e.target.value })}
                              placeholder='z.B. step3.output.matched == true   (leer = immer ausführen)'
                              disabled={readOnly}
                            />
                          </label>
                          <span className="workflow-step-runif-hint">
                            Gleiche Syntax wie bei <em>Bedingung</em>-Schritten — ohne <code>{'{{ }}'}</code>.
                            Wenn leer, läuft der Schritt immer.
                          </span>
                        </div>
                        <div className="workflow-step-id-hint">
                          Verweise mit <code>{`{{${step.id}.output}}`}</code> auf die Ausgabe.
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
          )}

          {activeTab === 'diagram' && (
            <div className="workflow-tab-panel">
              <div className="workflow-diagram-wrap">
                <p className="section-hint" style={{ marginBottom: 12 }}>
                  Visualisierung des Ablaufs. Schritte werden in der Reihenfolge ausgeführt, in der sie hier stehen.
                  Daten-Referenzen (<code>{'{{stepX.output.feld}}'}</code>) werden als Eingabe-Liste pro Schritt angezeigt.
                </p>
                <WorkflowDiagram steps={steps} />
              </div>
            </div>
          )}

          {activeTab === 'schedule' && (
            <div className="workflow-tab-panel">
              <div className="workflow-schedule-block">
                <div className="workflow-schedule-head">
                  <label className="workflow-schedule-toggle">
                    <input
                      type="checkbox"
                      checked={schedule.enabled}
                      onChange={e => setSchedule(s => ({ ...s, enabled: e.target.checked }))}
                      disabled={readOnly}
                    />
                    <span>⏱ Zeitplan aktivieren (automatisch ausführen)</span>
                  </label>
                </div>
                {!schedule.enabled && (
                  <p className="section-hint">
                    Aktiviere den Zeitplan, damit der Workflow automatisch zu festgelegten Zeiten läuft (z. B. täglich um 09:00).
                  </p>
                )}
                {schedule.enabled && (
                  <div className="workflow-schedule-grid">
                    <label className="data-source-field small">
                      <span>Häufigkeit</span>
                      <select
                        value={schedule.frequency}
                        onChange={e => setSchedule(s => ({ ...s, frequency: e.target.value }))}
                        disabled={readOnly}
                      >
                        <option value="hourly">Stündlich</option>
                        <option value="daily">Täglich</option>
                        <option value="weekly">Wöchentlich</option>
                      </select>
                    </label>
                    {schedule.frequency === 'weekly' && (
                      <label className="data-source-field small">
                        <span>Wochentag</span>
                        <select
                          value={schedule.dayOfWeek}
                          onChange={e => setSchedule(s => ({ ...s, dayOfWeek: Number(e.target.value) }))}
                          disabled={readOnly}
                        >
                          <option value={1}>Montag</option>
                          <option value={2}>Dienstag</option>
                          <option value={3}>Mittwoch</option>
                          <option value={4}>Donnerstag</option>
                          <option value={5}>Freitag</option>
                          <option value={6}>Samstag</option>
                          <option value={0}>Sonntag</option>
                        </select>
                      </label>
                    )}
                    <label className="data-source-field small">
                      <span>Start Time {schedule.frequency === 'hourly' && '(Minute = wann pro Stunde)'}</span>
                      <input
                        type="time"
                        className="text-input"
                        value={schedule.startTime}
                        onChange={e => setSchedule(s => ({ ...s, startTime: e.target.value }))}
                        disabled={readOnly}
                      />
                    </label>
                    <label className="data-source-field small">
                      <span>End Time (optional)</span>
                      <input
                        type="time"
                        className="text-input"
                        value={schedule.endTime}
                        onChange={e => setSchedule(s => ({ ...s, endTime: e.target.value }))}
                        disabled={readOnly}
                        placeholder="--:--"
                      />
                    </label>
                    {schedule.frequency === 'daily' && (
                      <label className="data-source-field small">
                        <span>Intervall in Minuten (optional)</span>
                        <input
                          type="number"
                          min="0"
                          max="1440"
                          className="text-input"
                          placeholder="leer = 1× pro Tag"
                          value={schedule.intervalMinutes || ''}
                          onChange={e => setSchedule(s => ({ ...s, intervalMinutes: Number(e.target.value) || 0 }))}
                          disabled={readOnly}
                        />
                      </label>
                    )}
                    <label className="data-source-field small">
                      <span>Max. pro Tag (0 = unbegrenzt)</span>
                      <input
                        type="number"
                        min="0"
                        className="text-input"
                        value={schedule.dailyLimit}
                        onChange={e => setSchedule(s => ({ ...s, dailyLimit: Number(e.target.value) || 0 }))}
                        disabled={readOnly}
                      />
                    </label>
                    <label className="data-source-field small">
                      <span>Startdatum (optional)</span>
                      <input
                        type="date"
                        className="text-input"
                        value={schedule.startDate}
                        onChange={e => setSchedule(s => ({ ...s, startDate: e.target.value }))}
                        disabled={readOnly}
                      />
                    </label>
                    <div className="workflow-schedule-hint">
                      {schedule.frequency === 'hourly' && `Läuft jede Stunde zur Minute :${(schedule.startTime || '00:00').split(':')[1] || '00'}`}
                      {schedule.frequency === 'daily' && schedule.intervalMinutes > 0 && (
                        `Läuft alle ${schedule.intervalMinutes} Min ab ${schedule.startTime}`
                      )}
                      {schedule.frequency === 'daily' && !schedule.intervalMinutes && schedule.dailyLimit > 1 && (
                        `Läuft ${schedule.dailyLimit}× pro Tag, gleichmäßig verteilt ab ${schedule.startTime}`
                      )}
                      {schedule.frequency === 'daily' && !schedule.intervalMinutes && schedule.dailyLimit <= 1 && (
                        `Läuft jeden Tag um ${schedule.startTime}`
                      )}
                      {schedule.frequency === 'weekly' && `Läuft wöchentlich ab ${schedule.startTime}`}
                      {schedule.endTime && ` bis ${schedule.endTime}`}
                      {schedule.dailyLimit > 0 && schedule.intervalMinutes > 0 && ` · max. ${schedule.dailyLimit}× pro Tag`}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {error && <div className="form-error">{error}</div>}
        </div>

        <div className="workflow-editor-footer">
          <button className="link-btn" onClick={onClose}>Schließen</button>
          {!readOnly && (
            <button className="primary-btn" onClick={handleSave} disabled={saving || !name.trim()}>
              {saving ? '…' : 'Speichern'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StepConfig({ step, idx, dataSources, models = [], departments = [], tenantMembers = [], updateConfig, readOnly }) {
  const c = step.config || {};
  const set = (k, v) => updateConfig(idx, k, v);
  const setJson = (k, v) => {
    try { set(k, JSON.parse(v)); } catch { set(k, v); }
  };
  const jsonValue = (v) => {
    if (typeof v === 'string') return v;
    try { return JSON.stringify(v ?? '', null, 2); } catch { return ''; }
  };

  if (step.type === 'ai_prompt') {
    // Wenn der gespeicherte model-Wert kein bekanntes Modell ist (z. B.
    // KI-Halluzination wie ".ai-text-generative" oder ein deinstalliertes
    // Modell), warnen wir und bieten 1-Klick-Reset an. Sonst läuft das
    // Workflow scheduled fehl, ohne dass der User es im Editor sieht.
    const modelMissing = c.model && !models.some(m => m.name === c.model);
    return (
      <div className="workflow-step-config">
        <label className="data-source-field">
          <span>Prompt</span>
          <textarea className="text-input" rows={4} value={c.prompt || ''} onChange={e => set('prompt', e.target.value)} disabled={readOnly} />
        </label>
        <label className="data-source-field small">
          <span>Modell (optional — leer = Runner-Auswahl)</span>
          <select value={modelMissing ? '' : (c.model || '')} onChange={e => set('model', e.target.value)} disabled={readOnly}>
            <option value="">— Standard (Runner-Auswahl) —</option>
            {models.map(m => (
              <option key={m.name} value={m.name}>
                {formatModelLabel(m)}
              </option>
            ))}
          </select>
        </label>
        {modelMissing && (
          <div className="form-error" style={{ marginTop: 4 }}>
            ⚠ Hier war zuvor das Modell <code>{c.model}</code> gespeichert — das gibt es nicht (mehr).
            Wähle oben ein verfügbares Modell oder lass „Standard" stehen, dann{' '}
            <button
              type="button"
              className="link-btn"
              style={{ display: 'inline', padding: 0 }}
              onClick={() => set('model', '')}
              disabled={readOnly}
            >
              klick hier zum Zurücksetzen
            </button>{' '}und speichere — der Wert wird dann auch in der DB sauber.
          </div>
        )}
      </div>
    );
  }

  if (step.type === 'http_request') {
    return (
      <div className="workflow-step-config">
        <div className="data-source-row">
          <label className="data-source-field small">
            <span>Methode</span>
            <select value={c.method || 'GET'} onChange={e => set('method', e.target.value)} disabled={readOnly}>
              {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(m => <option key={m}>{m}</option>)}
            </select>
          </label>
          <label className="data-source-field">
            <span>URL</span>
            <input className="text-input" value={c.url || ''} onChange={e => set('url', e.target.value)} disabled={readOnly} />
          </label>
        </div>
        <label className="data-source-field">
          <span>Headers (JSON)</span>
          <textarea className="text-input" rows={2} value={jsonValue(c.headers)} onChange={e => setJson('headers', e.target.value)} disabled={readOnly} />
        </label>
        <label className="data-source-field">
          <span>Body (JSON oder Text)</span>
          <textarea className="text-input" rows={3} value={jsonValue(c.body)} onChange={e => setJson('body', e.target.value)} disabled={readOnly} />
        </label>
      </div>
    );
  }

  if (step.type === 'data_source_query') {
    const ds = dataSources.find(d => d._id === c.dataSourceId);
    return (
      <div className="workflow-step-config">
        <label className="data-source-field">
          <span>Datenquelle</span>
          <select value={c.dataSourceId || ''} onChange={e => set('dataSourceId', e.target.value)} disabled={readOnly}>
            <option value="">— wählen —</option>
            {dataSources.map(d => <option key={d._id} value={d._id}>{d.name} ({d.type})</option>)}
          </select>
        </label>
        {ds?.type === 'sql' ? (
          <label className="data-source-field">
            <span>SQL-Query</span>
            <textarea className="text-input" rows={3} value={c.query || ''} onChange={e => set('query', e.target.value)} disabled={readOnly} />
          </label>
        ) : (
          <>
            <div className="data-source-row">
              <label className="data-source-field small">
                <span>Methode</span>
                <select value={c.method || 'GET'} onChange={e => set('method', e.target.value)} disabled={readOnly}>
                  {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(m => <option key={m}>{m}</option>)}
                </select>
              </label>
              <label className="data-source-field">
                <span>Pfad</span>
                <input className="text-input" value={c.path || ''} onChange={e => set('path', e.target.value)} placeholder="/api/v1/..." disabled={readOnly} />
              </label>
            </div>
            <label className="data-source-field">
              <span>Body (optional, JSON)</span>
              <textarea className="text-input" rows={2} value={jsonValue(c.body)} onChange={e => setJson('body', e.target.value)} disabled={readOnly} />
            </label>
          </>
        )}
      </div>
    );
  }

  if (step.type === 'transform') {
    return (
      <div className="workflow-step-config">
        <label className="data-source-field">
          <span>Vorlage (JSON; verwende {`{{ctx.path}}`})</span>
          <textarea className="text-input" rows={4} value={jsonValue(c.template)} onChange={e => setJson('template', e.target.value)} disabled={readOnly} />
        </label>
      </div>
    );
  }

  if (step.type === 'condition') {
    return (
      <div className="workflow-step-config">
        <label className="data-source-field">
          <span>Bedingung (z.B. <code>step1.output.status == "ok"</code>)</span>
          <input className="text-input" value={c.expression || ''} onChange={e => set('expression', e.target.value)} disabled={readOnly} />
        </label>
        <label className="data-source-field">
          <span>Wert wenn wahr (JSON)</span>
          <textarea className="text-input" rows={2} value={jsonValue(c.then)} onChange={e => setJson('then', e.target.value)} disabled={readOnly} />
        </label>
        <label className="data-source-field">
          <span>Wert wenn falsch (JSON)</span>
          <textarea className="text-input" rows={2} value={jsonValue(c.else)} onChange={e => setJson('else', e.target.value)} disabled={readOnly} />
        </label>
      </div>
    );
  }

  if (step.type === 'create_task') {
    // Genau eine Zuweisung — wenn die Person gesetzt wird, wird die Abteilung
    // geleert und umgekehrt.
    const setAssignee = (field, value) => {
      if (field === 'user' && value) {
        set('assigneeUserId', value);
        set('assigneeDepartmentId', '');
      } else if (field === 'department' && value) {
        set('assigneeDepartmentId', value);
        set('assigneeUserId', '');
      } else if (field === 'user') {
        set('assigneeUserId', '');
      } else if (field === 'department') {
        set('assigneeDepartmentId', '');
      }
    };
    const topDepts = departments.filter(d => !d.parentId);
    return (
      <div className="workflow-step-config">
        <label className="data-source-field">
          <span>Titel</span>
          <input className="text-input" value={c.title || ''} onChange={e => set('title', e.target.value)} disabled={readOnly} />
        </label>
        <div className="data-source-row">
          <label className="data-source-field">
            <span>Zuständige Person</span>
            <select
              value={c.assigneeUserId || ''}
              onChange={e => setAssignee('user', e.target.value)}
              disabled={readOnly || !!c.assigneeDepartmentId}
            >
              <option value="">— keine —</option>
              {tenantMembers.map(m => (
                <option key={m.userId} value={m.userId}>{m.username} ({m.email})</option>
              ))}
            </select>
          </label>
          <label className="data-source-field">
            <span>Zuständige Abteilung</span>
            <select
              value={c.assigneeDepartmentId || ''}
              onChange={e => setAssignee('department', e.target.value)}
              disabled={readOnly || !!c.assigneeUserId}
            >
              <option value="">— keine —</option>
              {topDepts.map(top => {
                const subs = departments.filter(d => d.parentId === top._id);
                return (
                  <optgroup key={top._id} label={top.name}>
                    <option value={top._id}>{top.name} (Top-Ebene)</option>
                    {subs.map(s => (
                      <option key={s._id} value={s._id}>↳ {s.name}</option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
          </label>
          <label className="data-source-field small">
            <span>Fällig am</span>
            <input
              type="date"
              className="text-input"
              value={c.due ? String(c.due).slice(0, 10) : ''}
              onChange={e => set('due', e.target.value)}
              disabled={readOnly}
            />
          </label>
        </div>
        <p className="workflow-ai-hint" style={{ display: 'block', marginTop: -4 }}>
          {(!c.assigneeUserId && !c.assigneeDepartmentId)
            ? 'Tipp: Genau eine Zuweisung wählen (Person oder Abteilung). Bleibt beides leer, ist die Aufgabe „nicht zugewiesen" und nur für Admins mit „Alle Aufgaben"-Recht sichtbar.'
            : 'Wechsel der Zuweisung: leere die andere Auswahl, dann ist das jeweils andere Feld wieder aktiv.'}
        </p>
        <label className="data-source-field">
          <span>Payload (JSON)</span>
          <textarea className="text-input" rows={2} value={jsonValue(c.payload)} onChange={e => setJson('payload', e.target.value)} disabled={readOnly} />
        </label>
      </div>
    );
  }

  if (step.type === 'email_send') {
    return (
      <div className="workflow-step-config">
        <div className="data-source-row">
          <label className="data-source-field">
            <span>An (to) — kommagetrennt für mehrere</span>
            <input
              className="text-input"
              placeholder="empfaenger@example.com"
              value={c.to || ''}
              onChange={e => set('to', e.target.value)}
              disabled={readOnly}
            />
          </label>
          <label className="data-source-field">
            <span>Antwort an (replyTo) — optional</span>
            <input
              className="text-input"
              value={c.replyTo || ''}
              onChange={e => set('replyTo', e.target.value)}
              disabled={readOnly}
            />
          </label>
        </div>
        <div className="data-source-row">
          <label className="data-source-field">
            <span>CC (optional)</span>
            <input className="text-input" value={c.cc || ''} onChange={e => set('cc', e.target.value)} disabled={readOnly} />
          </label>
          <label className="data-source-field">
            <span>BCC (optional)</span>
            <input className="text-input" value={c.bcc || ''} onChange={e => set('bcc', e.target.value)} disabled={readOnly} />
          </label>
        </div>
        <label className="data-source-field">
          <span>Betreff</span>
          <input
            className="text-input"
            placeholder="Neue Aufgabe: {{step1.output.text}}"
            value={c.subject || ''}
            onChange={e => set('subject', e.target.value)}
            disabled={readOnly}
          />
        </label>
        <label className="data-source-field">
          <span>Nachricht</span>
          <textarea
            className="text-input"
            rows={5}
            placeholder="Hallo,&#10;&#10;{{step1.output.text}}&#10;&#10;Grüße"
            value={c.body || ''}
            onChange={e => set('body', e.target.value)}
            disabled={readOnly}
          />
        </label>
        <p className="workflow-ai-hint" style={{ display: 'block' }}>
          Nutzt die SMTP-Konfiguration des Mandanten (Mandanten-Einstellungen → E-Mail-Einstellungen).
          Templates wie <code>{'{{step1.output.text}}'}</code> in Betreff/Nachricht funktionieren wie überall.
        </p>
      </div>
    );
  }

  return null;
}
